import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  Experiment,
  ExperimentClient,
  ExperimentUser,
} from '@amplitude/experiment-react-native-client';
import fastDeepEqual from 'fast-deep-equal';

import { getCurrentSession } from 'remote/auth';
import { EventTypes, logEvent } from 'state/amplitude';
import { useCognitoId } from 'state/auth/auth-sync-module';
import AuthStorage from 'utils/cognito/storage';
import { LogLevel } from 'utils/datadog/types';
import { getDeviceIdAsync } from 'utils/device-id';
import { getApiKey, minDevLogLevel } from 'utils/environment';
import { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';

import { RunningExperiments } from './running-experiments';
import { IExperimentsContext, VariantOptions } from './types';

export { RunningExperiments } from './running-experiments';

const Context = React.createContext<IExperimentsContext>({
  isLoading: false,
  getVariantValue: (_flag: string | RunningExperiments) => undefined,
  logExposure: (_flag: RunningExperiments) => void 0,
  variant: (_flag: RunningExperiments, _expectedVariant: string, _options?: VariantOptions) =>
    false,
});

export const useExperiments = () => useContext(Context);

const experiment = Experiment.initializeWithAmplitudeAnalytics(
  getApiKey({ key: 'amplitudeExperiments' }),
  {
    debug: __DEV__ && minDevLogLevel() <= LogLevel.debug,
    automaticExposureTracking: false, // we track this manually for many experiments
    automaticFetchOnAmplitudeIdentityChange: true,
  }
);

// app start fetch initiated outside of react for performance
let isExperimentsFetchPending = true;
const getInitialUserId = async (): Promise<string | null> => {
  await AuthStorage.sync();
  const storedToken = AuthStorage.getItem(StorageKeys.USER_AUTH_TOKEN);
  if (storedToken) {
    return storedToken;
  }

  // Fallback: If the user is logged in via Cognito but our local storage sync hasn't
  // picked up the USER_AUTH_TOKEN yet (race condition), we try to get the session directly.
  // This prevents the "Guest -> User" variant jump on app launch.
  try {
    const session = await getCurrentSession();
    return session?.getAccessToken().payload.username || null;
  } catch {
    return null;
  }
};

export const experimentsFetchPromise = fetchExperimentsForUser(getInitialUserId()).finally(() => {
  isExperimentsFetchPending = false;
});

let previousCognitoId: string | null = null;

async function fetchExperimentsForUser(
  userIdPromise: Promise<string | null>
): Promise<ExperimentClient> {
  const [deviceId, userId] = await Promise.all([getDeviceIdAsync(), userIdPromise]);

  const user: ExperimentUser = {
    device_id: deviceId.toLowerCase(),
  };

  previousCognitoId = userId;

  if (userId) {
    user.user_id = userId;
  }

  return experiment.fetch(user);
}

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const cognitoId = useCognitoId();
  const flagExposuresHandled = useRef(new Map<string, boolean>());
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState(experiment.all()); // from localstorage initially or maybe get lucky and from server if very fast

  const isInitialCognitoEffect = useRef(true);

  useEffect(() => {
    // Clear on sign-out
    if (!isInitialCognitoEffect.current && previousCognitoId && !cognitoId) {
      experiment.clear();
      flagExposuresHandled.current.clear();
    }

    let fetchPromise: Promise<ExperimentClient>;
    if (isInitialCognitoEffect.current) {
      fetchPromise = experimentsFetchPromise;
      isInitialCognitoEffect.current = false;
    } else if ((cognitoId || null) !== (previousCognitoId || null)) {
      // User changed

      if (isExperimentsFetchPending) {
        // If the global fetch is still pending, we assume it covers the current user state
        // (since both rely on the same AuthStorage source of truth).
        // This prevents a double fetch when cognitoId updates from null -> value during startup.
        return;
      }

      fetchPromise = fetchExperimentsForUser(Promise.resolve(cognitoId || null));
    } else {
      // No fetch needed
      return;
    }

    setIsLoading(true);
    fetchPromise
      .then(result => {
        const newVariants = result.all();
        setVariants(prevVariants => {
          const equal = fastDeepEqual(newVariants, prevVariants);
          return equal ? prevVariants : newVariants;
        });
      })
      .catch(error => logger.error({ error, message: 'Amplitude experiments init failure' }))
      .finally(() => setIsLoading(false));
  }, [cognitoId]);

  // Override the typical getVariant function to utilize our local cache approach.
  const value = useMemo(() => {
    const logExposure = (
      flagKey: string | RunningExperiments,
      type: 'Manual' | 'Auto' = 'Manual'
    ) => {
      const foundVariant = variants[flagKey];
      const foundValue = foundVariant?.value;
      const flagAndValueKey = flagKey + foundVariant?.value;

      if (!flagExposuresHandled.current.has(flagAndValueKey) && foundValue) {
        logEvent('$exposure', EventTypes.Other, {
          flag_key: flagKey,
          variant: foundValue,
        });
        flagExposuresHandled.current.set(flagAndValueKey, true);
        logger.info(
          `[Experiment] ${type} Logging exposure for "${flagKey}", variant value: "${foundVariant.value}"`
        );
      }
    };

    return {
      isLoading,
      getVariantValue: (flagKey: string | RunningExperiments) => variants[flagKey],
      logExposure,
      variant: (
        flagKey: string,
        expectedVariant: string,
        options = { skipExposureLogging: false }
      ) => {
        if (options.skipExposureLogging !== true) {
          logExposure(flagKey, 'Auto');
        }

        const foundVariant = variants[flagKey];
        return foundVariant?.value === expectedVariant;
      },
    };
  }, [variants, isLoading]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
